import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router";

import { Box, Divider } from "@mui/material";
import { AppContext } from "App";
import EmptyCard from "components/Cards/EmptyCard";
import { WORKSPACE_VIEW_CARD, WORKSPACE_VIEW_TABLE } from "components/Workspaces/helpers";
import WorkspacePageHeader from "components/Workspaces/WorkspacePageHeader";
import { enqueueSnackbar } from "notistack";

import { UsersApiHelperForMember } from "../../api/ApiHelper";
import EquipmentLoanCard from "./EquipmentLoanCard";
import EquipmentLoanDataTable from "./EquipmentLoanDataTable";

const CARD_GRID_KEY = "equipment_inventory_loans";

export default function EquipmentInventoryLoansSection({ equipmentId, onDeleteLoan, onLoanCreated }) {
  const { globalState } = useContext(AppContext);
  const [workspaceMode, setWorkspaceMode] = useState(WORKSPACE_VIEW_TABLE);
  const [loans, setLoans] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (onLoanCreated) {
      setLoans((prev) => {
        const exists = prev.some((l) => l.id === onLoanCreated.id);
        if (exists) {
          return prev.map((l) => (l.id === onLoanCreated.id ? onLoanCreated : l));
        }
        return [...prev, onLoanCreated];
      });
    }
  }, [onLoanCreated]);

  const loadLoans = useCallback(async () => {
    if (!globalState?.producer_token || !globalState?.crop_year || !equipmentId) {
      setLoans([]);
      return;
    }

    try {
      setIsLoading(true);
      const filter = {
        producer_token: globalState.producer_token,
        year: globalState.crop_year,
        is_active: true,
        equipment_inventory_id: equipmentId,
      };

      const debtSchedules = await UsersApiHelperForMember.callPost("debtSchedule/Search", filter, globalState);
      setLoans(debtSchedules || []);
    } catch (err) {
      console.warn("Error loading loans:", err);
      enqueueSnackbar(err.detail || err.message || "Error loading loans", {
        variant: "error",
      });
      setLoans([]);
    } finally {
      setIsLoading(false);
    }
  }, [equipmentId, globalState?.producer_token, globalState?.crop_year]);

  useEffect(() => {
    loadLoans();
  }, [loadLoans]);

  const activeLoans = useMemo(() => {
    return loans?.filter((loan) => loan.is_active !== false) || [];
  }, [loans]);
  const navigate = useNavigate();

  const handleEdit = (loan) => {
    navigate(`/financial/equipment/${equipmentId}/${loan.id}`);
    //onEditLoan?.(loan);
  };

  const handleDelete = async (loan) => {
    if (!globalState?.producer_token || !loan?.id) {
      enqueueSnackbar("Unable to delete loan: missing required information", {
        variant: "error",
      });
      return;
    }

    try {
      await UsersApiHelperForMember.callPost(
        "debtSchedule",
        {
          DebtSchedule: [{ ...loan, is_active: false }],
          document_id: loan.document_id,
          producer_token: globalState.producer_token,
        },
        globalState
      );

      await loadLoans();
      onDeleteLoan();
    } catch (err) {
      console.warn("Error deleting loan:", err);
      enqueueSnackbar(err.detail || err.message || "Error deleting loan", {
        variant: "error",
        preventDuplicate: true,
      });
    }
  };
  return (
    <>
      <Divider sx={{ my: 3 }} />
      <WorkspacePageHeader
        title="Loans"
        viewMode={workspaceMode}
        onViewModeChange={setWorkspaceMode}
        cardGridKey={CARD_GRID_KEY}
      />

      {workspaceMode === WORKSPACE_VIEW_CARD && (
        <Box className="cardContainer">
          {activeLoans.map((loan) => (
            <EquipmentLoanCard key={loan.id} loan={loan} onEdit={handleEdit} onDelete={handleDelete} />
          ))}

          {!loans?.length && !isLoading && (
            <EmptyCard text="No loans" secondaryText="Get started by adding a loan" isLoading={isLoading} />
          )}
        </Box>
      )}

      {workspaceMode === WORKSPACE_VIEW_TABLE && (
        <EquipmentLoanDataTable loans={activeLoans} onEdit={handleEdit} onDelete={handleDelete} isLoading={isLoading} />
      )}
    </>
  );
}
